import {
  AddAppAccountRequestPayload,
  TermsConsent,
} from "@/data/payload/app-account-setting/add-app-account-request-payload";
import { CheckUserIdAvailableRequestPayload } from "@/data/payload/app-account-setting/check-user-id-available-request-payload";
import { POLICY_CONSENT_URL, URLList } from "@/data/url_list";
import { ParentalConsentInfo } from "@/domain/repository/app-account-setting-repository";
import { isValidAccountSetupToken } from "@/utils/security/account-setup-token-validator";
import { errorConverter, SATError, SATErrorModel, SATErrorType, SATResult } from "@sadtx/sat-error";

const AUTH_TOKEN_STORAGE_KEY = "app-account-setting-token";
const TERMS_CONSENT_STORAGE_KEY = "app-account-setting-terms-consent";
const POLICY_CONSENT_VERSION = "1.0";

export class AppAccountSettingSource {
  async setAuthorizedToken(token: string): Promise<SATResult<void, SATError>> {
    const linkToken = token.trim();
    if (!linkToken) {
      return SATResult.failure(
        new SATError(SATErrorType.ExpiredAuthCodeToken, "Account setup token is empty"),
      );
    }
    if (!isValidAccountSetupToken(linkToken)) {
      return SATResult.failure(
        new SATError(SATErrorType.ExpiredAuthCodeToken, "Invalid account setup token"),
      );
    }

    sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, linkToken);
    return SATResult.success(void 0);
  }

  async isExistAuthorizedToken(): Promise<boolean> {
    try {
      this.getAuthorizedToken();
      return true;
    } catch {
      return false;
    }
  }

  async removeAuthorizedToken(): Promise<void> {
    sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TERMS_CONSENT_STORAGE_KEY);
  }

  async saveTermsConsent(termsConsent: TermsConsent[]): Promise<SATResult<void, SATError>> {
    try {
      sessionStorage.setItem(TERMS_CONSENT_STORAGE_KEY, JSON.stringify(termsConsent));
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async checkUserNameBySmsTokenForLoginAccount(name: string): Promise<SATResult<void, SATError>> {
    try {
      const token = this.getAuthorizedToken();
      const params = new URLSearchParams({ token, name });
      const url = `${URLList.AUTH.VERIFY_ACCOUNT_SETUP_IDENTITY}?${params.toString()}`;
      const data = asRecord(await this.request(url, { method: "GET" }));
      const accountSetupToken = typeof data.token === "string" ? data.token : "";
      if (!accountSetupToken) {
        throw new SATError(SATErrorType.ExpiredAuthCodeToken, "Account setup token is empty");
      }
      sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, accountSetupToken);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async addAppAccount(payload: AddAppAccountRequestPayload): Promise<SATResult<void, SATError>> {
    try {
      const token = this.getAuthorizedToken();
      await this.request(URLList.AUTH.COMPLETE_APP_ACCOUNT_SETUP, {
        method: "PUT",
        body: JSON.stringify({
          token,
          loginId: payload.loginId,
          newSn: payload.newSn,
        }),
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      if (import.meta.env.MODE === "dev") return SATResult.success(void 0);
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async checkUserIdAvailable(
    payload: CheckUserIdAvailableRequestPayload,
  ): Promise<SATResult<void, SATError>> {
    try {
      const loginId = payload.loginId.trim().toLowerCase();
      const url = `${URLList.AUTH.CHECK_USER_ID_AVAILABLE}/${encodeURIComponent(loginId)}`;
      await this.request(url, { method: "GET" });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async fetchPrivayMdFile(locale: string): Promise<SATResult<ParentalConsentInfo, SATError>> {
    try {
      const url = POLICY_CONSENT_URL.Privacy(locale);
      return SATResult.success(await this.fetchPolicyConsentFile(url));
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async fetchTermsOfServiceFile(locale: string): Promise<SATResult<ParentalConsentInfo, SATError>> {
    try {
      const url = POLICY_CONSENT_URL.TermsOfService(locale);
      return SATResult.success(await this.fetchPolicyConsentFile(url));
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  private getAuthorizedToken(): string {
    const token = sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token || token === "undefined" || !token.trim()) {
      throw new SATErrorModel.ExpiredAuthCodeToken();
    }
    return token;
  }

  private getTermsConsent(): TermsConsent[] {
    try {
      const termsConsent = sessionStorage.getItem(TERMS_CONSENT_STORAGE_KEY);
      if (!termsConsent || termsConsent === "undefined" || !termsConsent.trim()) {
        throw new SATError(SATErrorType.ValidationError, "Terms consent is empty");
      }

      const parsed: unknown = JSON.parse(termsConsent);
      if (!Array.isArray(parsed)) {
        throw new SATError(SATErrorType.ValidationError, "Terms consent is invalid");
      }
      return parsed as TermsConsent[];
    } catch (error: unknown) {
      if (error instanceof SATError) throw error;
      throw new SATError(SATErrorType.ValidationError, "Failed to read terms consent");
    }
  }

  private async fetchPolicyConsentFile(url: string): Promise<ParentalConsentInfo> {
    const response = await fetch(`${url}?_t=${Date.now()}`, {
      method: "GET",
      cache: "no-store",
    });

    if (!response.ok) {
      const data = await response.text().catch(() => "");
      throw { response: { data, status: response.status } };
    }

    return {
      version: response.headers.get("x-ms-meta-version") ?? POLICY_CONSENT_VERSION,
      content: await response.text(),
    };
  }

  private async request(url: string, init: RequestInit): Promise<unknown> {
    const response = await fetch(url, {
      ...init,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(init.headers ?? {}),
      },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw { response: { data, status: response.status } };
    }
    return response.json().catch(() => ({}));
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}
